import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import type { WorkflowRunNodeStatus } from '@/apis/logiblocksAPI';
import useUserDateTimeFormat from '@/hooks/useUserDateTimeFormat';
import { getLBNodeInfo } from '../nodes/registry';
import type { ReactFlowNode } from '../types';
import { RunCompletedIcon, RunFailedIcon } from './runStatusIcons';
import { getRunStepName, type RunStep } from './runStepUtils';
import { formatDuration } from './WorkflowRunHistoryPanel';
import styles from './RunStepList.module.css';

/** Shows a step's status: a check once its node finished, a warning if it failed, or a spinner. */
function StepStatusIcon({ status }: { status: WorkflowRunNodeStatus }) {
  switch (status) {
    case 'COMPLETED':
    case 'SKIPPED_CHILDREN':
      return (
        <RunCompletedIcon
          className={`${styles.statusIcon} ${styles.completedIcon}`}
          size={14}
        />
      );
    case 'FAILED':
      return (
        <RunFailedIcon
          className={`${styles.statusIcon} ${styles.failedIcon}`}
          size={14}
        />
      );
    case 'RUNNING':
      return (
        <Loader2
          aria-label='Running'
          className={`${styles.statusIcon} ${styles.runningIcon}`}
          size={14}
        />
      );
    default:
      console.error(`Unexpected run step status: ${String(status)}`);
      return (
        <Loader2
          aria-label='Running'
          className={`${styles.statusIcon} ${styles.runningIcon}`}
          size={14}
        />
      );
  }
}

/** Shows one step: its status, name, summary and duration, and its detail when expanded. */
function RunStepRow({
  step,
  runGraphNodes,
  dateFormat,
  isExpanded,
  onToggle,
}: {
  step: RunStep;
  runGraphNodes: ReactFlowNode[];
  dateFormat: string | undefined;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const nodeInfo =
    step.nodeData === null ? undefined : getLBNodeInfo(step.nodeData);
  const summary = nodeInfo?.getRunStepSummary?.(step, dateFormat) ?? null;
  const DetailComponent = nodeInfo?.RunStepDetailComponent;
  const headerContent = (
    <>
      <StepStatusIcon status={step.status} />
      <span className={styles.stepText}>
        <span className={styles.stepName}>{getRunStepName(step)}</span>
        {summary !== null && (
          <span className={styles.stepSummary}>{summary}</span>
        )}
      </span>
      <span className={styles.stepDuration}>
        {formatDuration(step.durationMs)}
      </span>
    </>
  );
  return (
    <li className={styles.step}>
      {DetailComponent === undefined ? (
        <div className={styles.stepHeader}>{headerContent}</div>
      ) : (
        <button
          type='button'
          className={styles.stepHeader}
          aria-expanded={isExpanded}
          onClick={onToggle}
        >
          {headerContent}
        </button>
      )}
      {DetailComponent !== undefined && isExpanded && (
        <div className={styles.stepDetail}>
          <DetailComponent
            step={step}
            runGraphNodes={runGraphNodes}
            dateFormat={dateFormat}
          />
        </div>
      )}
    </li>
  );
}

/**
 * Lists a run's steps, each with its status, name, summary and duration. A step whose node type
 * has a detail view expands to show it.
 */
export function RunStepList({
  steps,
  runGraphNodes,
}: {
  steps: RunStep[];
  /** The nodes of the graph the run executed. */
  runGraphNodes: ReactFlowNode[];
}) {
  const { dateFormat } = useUserDateTimeFormat();
  const [expandedStepKeys, setExpandedStepKeys] = useState<ReadonlySet<string>>(
    new Set()
  );

  /** Expands the step with `stepKey` if it is collapsed, and collapses it otherwise. */
  function toggleStep(stepKey: string): void {
    setExpandedStepKeys((current) => {
      const next = new Set(current);
      if (next.has(stepKey)) {
        next.delete(stepKey);
      } else {
        next.add(stepKey);
      }
      return next;
    });
  }

  return (
    <ol className={styles.stepList}>
      {steps.map((step) => {
        // A loop runs a node once per iteration, so the node id alone repeats.
        const stepKey = `${step.nodeId}:${step.startedAt}`;
        return (
          <RunStepRow
            key={stepKey}
            step={step}
            runGraphNodes={runGraphNodes}
            dateFormat={dateFormat}
            isExpanded={expandedStepKeys.has(stepKey)}
            onToggle={() => toggleStep(stepKey)}
          />
        );
      })}
    </ol>
  );
}
